import React, { useCallback } from "react";
import { Box, useColorModeValue } from "@chakra-ui/react";
import { useConversationContext } from "screens/thread/ConversationContext";

interface FilterButtonsProps {
  uniqueFilters: string[];
  highlightedFilters?: string[];
  size?: "sm" | "md";
}

export const FilterButtons: React.FC<FilterButtonsProps> = ({ uniqueFilters, size = "sm", highlightedFilters = [] }) => {
  const bgColor = useColorModeValue("white", "gray.700");
  const bgHighlightColor = useColorModeValue("yellow.100", "yellow.600");
  const activeColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const isMdSize = size === "md";
  const filterButtonPx = isMdSize ? "10px" : "5px";
  const filterButtonPy = isMdSize ? "3px" : "1px";
  const filterButtonFontSize = isMdSize ? "13px" : "10px";
  const filterButtonBorderRadius = isMdSize ? "lg" : "md";
  const { activeFilter, setActiveFilter } = useConversationContext();

  const handleFilterChange = useCallback(
    (filter: string | null) => {
      setActiveFilter(filter);
    },
    [setActiveFilter]
  );

  return (
    <>
      <Box
        as="button"
        px={filterButtonPx}
        py={filterButtonPy}
        borderRadius={filterButtonBorderRadius}
        fontSize={filterButtonFontSize}
        whiteSpace="nowrap"
        fontWeight={activeFilter === null ? "semibold" : "normal"}
        bg={activeFilter === null ? "primary.default" : bgColor}
        color={activeFilter === null ? activeColor : "gray.500"}
        _hover={{ color: activeFilter === null ? "gray.50" : "primary.default" }}
        _active={{}}
        border="1px solid"
        borderColor={borderColor}
        onClick={(e) => {
          e.stopPropagation();
          handleFilterChange(null);
        }}
        transition="all 0.2s"
        style={{ scrollSnapAlign: "start" }}>
        ALL
      </Box>
      {uniqueFilters.map((filter) => {
        const isActive = activeFilter === filter;
        const isHighlighted = !isActive && highlightedFilters.includes(filter);
        return (
          <Box
            className={`ch-filter-ticker-button ch-filter-deal ch-filter-ticker-${activeFilter === filter ? "active" : "inactive"}`}
            key={filter}
            as="button"
            px={filterButtonPx}
            py={filterButtonPy}
            borderRadius={filterButtonBorderRadius}
            fontSize={filterButtonFontSize}
            whiteSpace="nowrap"
            fontWeight={activeFilter === filter ? "semibold" : "normal"}
            bg={isActive ? "primary.default" : isHighlighted ? bgHighlightColor : bgColor}
            color={isActive ? activeColor : isHighlighted ? "gray.800" : "gray.500"}
            _active={{}}
            _hover={{
              color: isActive ? "gray.50" : isHighlighted ? "gray.900" : "primary.default",
            }}
            border="1px solid"
            borderColor={borderColor}
            onClick={(e) => {
              e.stopPropagation();
              handleFilterChange(filter);
            }}
            transition="all 0.2s"
            style={{ scrollSnapAlign: "start" }}>
            {filter.toUpperCase().replace(/_/g, " ")}
          </Box>
        );
      })}
    </>
  );
};
